/**
 * TenantConfigContext — EPIC-09-07: Per-tenant feature flags and branding.
 *
 * Fetches GET /api/v1/tenant/config on app mount and exposes feature_flags
 * and branding to all components via useTenantConfig().
 *
 * CSS variable injection: sets --color-primary when branding.primary_color is set.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import apiClient from '../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeatureFlags {
  course_types: string[];
  self_enroll: boolean;
  leaderboard: boolean;
  ai_recommendations: boolean;
  certificate_download: boolean;
}

export interface Branding {
  company_name: string;
  logo_url: string | null;
  primary_color: string;
}

interface TenantConfig {
  featureFlags: FeatureFlags;
  branding: Branding;
  isLoaded: boolean;
}

// ---------------------------------------------------------------------------
// Defaults (used before the fetch resolves or on error)
// ---------------------------------------------------------------------------

const DEFAULT_FLAGS: FeatureFlags = {
  course_types: ['roleplay', 'quiz', 'reading', 'blended', 'scenario_challenge'],
  self_enroll: true,
  leaderboard: false,
  ai_recommendations: false,
  certificate_download: true,
};

const DEFAULT_BRANDING: Branding = {
  company_name: 'ARIA',
  logo_url: null,
  primary_color: '#2563EB',
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const TenantConfigContext = createContext<TenantConfig>({
  featureFlags: DEFAULT_FLAGS,
  branding: DEFAULT_BRANDING,
  isLoaded: false,
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ProviderProps {
  children: ReactNode;
}

export function TenantConfigProvider({ children }: ProviderProps): JSX.Element {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    apiClient
      .get<{ data: { feature_flags: FeatureFlags; branding: Branding } }>(
        '/tenant/config',
      )
      .then((res) => {
        const { feature_flags, branding: b } = res.data.data;
        setFeatureFlags({ ...DEFAULT_FLAGS, ...feature_flags });
        setBranding({ ...DEFAULT_BRANDING, ...b });
        // Inject CSS variable for primary colour
        if (b?.primary_color) {
          document.documentElement.style.setProperty(
            '--color-primary',
            b.primary_color,
          );
        }
      })
      .catch(() => {
        // Silently fall back to defaults — non-fatal
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, []);

  return (
    <TenantConfigContext.Provider value={{ featureFlags, branding, isLoaded }}>
      {children}
    </TenantConfigContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTenantConfig(): TenantConfig {
  return useContext(TenantConfigContext);
}
